import { Injectable } from '@angular/core';
import { SocketIoService } from 'src/app/shared/socket-io/socket-io.service';

import {
  Feature,
  LiveUpdatesDeviceFeaturesQuery,
  LiveUpdatesGatewayAggregatedStatusQuery,
  LiveUpdatesGatewayFeaturesQuery,
  LiveUpdatesSmartNoteQuery,
  LiveUpdatesSubscriptionType,
} from '@vi-iot/client';

import { LiveUpdatesDataService } from './live-updates-data.service';
import { RichDevice } from '../../@store/devices/devices.state';

export enum LiveUpdatesUnsubscribeType {
  InstallationAggregatedStatus,
  InstallationErrors,
  SmartNote,
  Gateway,
  RemoteDevice,
}

@Injectable({
  providedIn: 'root',
})
export class LiveUpdatesService {
  operatingModesQueries: {
    [key: number]: { [key: string]: LiveUpdatesDeviceFeaturesQuery[] };
  } = {};
  deviceQuery: LiveUpdatesDeviceFeaturesQuery[];
  gatewayQuery: LiveUpdatesGatewayFeaturesQuery[];
  roomsQuery: LiveUpdatesDeviceFeaturesQuery[];
  remoteDeviceQuery: { [key: string]: LiveUpdatesDeviceFeaturesQuery[] } = {};
  aggregatedStatusesQuery: {
    [gatewaySerial: string]: LiveUpdatesGatewayAggregatedStatusQuery[];
  } = {};
  aggregatedStatusQuery: LiveUpdatesGatewayAggregatedStatusQuery[];
  installationSmartNotesQuery: LiveUpdatesSmartNoteQuery[];

  constructor(
    private socketIoService: SocketIoService,
    private liveUpdatesDataService: LiveUpdatesDataService
  ) {
    this.liveUpdatesDataService.subscribe();
  }

  subscribeOperatingModesFeatures(payload: {
    installationId: number;
    gatewaySerial: string;
    pairingId: string;
    operatingModes: {
      circuits: string[];
      mode: { [key: string]: Feature<unknown, unknown> };
    };
  }): void {
    if (this.operatingModesQueries[payload.installationId]) {
      for (const circuitId in this.operatingModesQueries[payload.installationId]) {
        if (this.operatingModesQueries[payload.installationId][circuitId]) {
          const query = this.operatingModesQueries[payload.installationId][circuitId];
          this.socketIoService.removeSingleQuery(query);
          delete this.operatingModesQueries[payload.installationId][circuitId];
        }
      }
      this.operatingModesQueries[payload.installationId] = {};
    }

    payload.operatingModes.circuits.forEach((circuitId) => {
      if (payload.operatingModes.mode[circuitId].isEnabled) {
        const query: LiveUpdatesDeviceFeaturesQuery[] = [
          {
            id: payload.pairingId,
            type: LiveUpdatesSubscriptionType.DeviceFeatures,
            gatewayId: payload.gatewaySerial,
            values: [payload.operatingModes.mode[circuitId].feature],
            version: '2',
          },
        ];
        this.operatingModesQueries[payload.installationId][circuitId] = query;
        this.socketIoService.updateQuery$.next(
          this.operatingModesQueries[payload.installationId][circuitId]
        );
      }
    });
  }

  subscribeDeviceFeatures(deviceEntities: RichDevice[]): void {
    const query: LiveUpdatesDeviceFeaturesQuery[] = deviceEntities.map((device) => {
      return {
        id: device.id,
        type: LiveUpdatesSubscriptionType.DeviceFeatures,
        gatewayId: device.gatewaySerial,
        version: '2',
      };
    });
    this.deviceQuery = query;
    this.socketIoService.updateQuery$.next(this.deviceQuery);
  }

  subscribeGatewayFeatures(gatewaySerials: string[]): void {
    const queries: LiveUpdatesGatewayFeaturesQuery[] = gatewaySerials.map((gatewaySerial) => ({
      id: gatewaySerial,
      type: LiveUpdatesSubscriptionType.GatewayFeatures,
      version: '2',
    }));

    this.gatewayQuery = queries;
    this.socketIoService.updateQuery$.next(queries);
  }

  subscribeInstallationsAggregatedStatus(gatewaySerial: string): void {
    if (gatewaySerial) {
      const query: LiveUpdatesGatewayAggregatedStatusQuery[] = [
        this.createAggregatedStatusSubscription(gatewaySerial),
      ];

      this.aggregatedStatusesQuery[gatewaySerial] = query;
      this.socketIoService.updateQuery$.next(this.aggregatedStatusesQuery[gatewaySerial]);
    }
  }

  subscribeInstallationAggregatedStatus(gatewaySerials: string[]): void {
    if (gatewaySerials?.length) {
      if (this.aggregatedStatusQuery) {
        this.socketIoService.removeSingleQuery(this.aggregatedStatusQuery);
        this.aggregatedStatusQuery = undefined;
      }
      const query: LiveUpdatesGatewayAggregatedStatusQuery[] = gatewaySerials.map(
        this.createAggregatedStatusSubscription
      );

      this.aggregatedStatusQuery = query;
      this.socketIoService.updateQuery$.next(this.aggregatedStatusQuery);
    }
  }

  subscribeInstallationSmartNotes(installationId: string): void {
    if (this.installationSmartNotesQuery) {
      this.socketIoService.removeSingleQuery(this.installationSmartNotesQuery);
      this.installationSmartNotesQuery = undefined;
    }
    const query: LiveUpdatesSmartNoteQuery[] = [
      {
        id: installationId,
        type: LiveUpdatesSubscriptionType.SmartNote,
        version: '1',
      },
    ];

    this.installationSmartNotesQuery = query;
    this.socketIoService.updateQuery$.next(this.installationSmartNotesQuery);
  }

  unsubscribeOperatingModesFeatures(installationId: number): void {
    this.socketIoService.disconnect();

    for (const circuitId in this.operatingModesQueries[installationId]) {
      if (this.operatingModesQueries[installationId][circuitId]) {
        const query = this.operatingModesQueries[installationId][circuitId];
        this.socketIoService.removeSingleQuery(query);
        delete this.operatingModesQueries[installationId][circuitId];
      }
    }
    this.operatingModesQueries[installationId] = {};
  }

  unsubscribeInstallationsAggregatedStatus(gatewaySerial: string): void {
    this.socketIoService.disconnect();

    if (!this.aggregatedStatusesQuery[gatewaySerial]) {
      return;
    }

    delete this.aggregatedStatusesQuery[gatewaySerial];
  }

  unsubscribeFeatures(type: LiveUpdatesUnsubscribeType): void {
    this.socketIoService.disconnect();
    let queries = [];

    switch (type) {
      case LiveUpdatesUnsubscribeType.InstallationAggregatedStatus:
        queries = [this.aggregatedStatusQuery];
        break;

      case LiveUpdatesUnsubscribeType.SmartNote:
        queries = [this.installationSmartNotesQuery];
        break;

      case LiveUpdatesUnsubscribeType.Gateway:
        queries = [this.gatewayQuery];
        break;

      case LiveUpdatesUnsubscribeType.RemoteDevice:
        queries = [this.remoteDeviceQuery];
        for (const deviceId in this.remoteDeviceQuery) {
          if (this.remoteDeviceQuery[deviceId]) {
            queries.push(this.remoteDeviceQuery[deviceId]);
          }
        }
        break;
    }

    queries.forEach((query) => {
      if (query) {
        this.socketIoService.removeSingleQuery(query);
        query = undefined;
      }
    });
  }

  private createAggregatedStatusSubscription(
    gatewaySerial: string
  ): LiveUpdatesGatewayAggregatedStatusQuery {
    return {
      id: gatewaySerial,
      type: LiveUpdatesSubscriptionType.GatewayAggregatedStatus,
      version: '1',
    };
  }
}
